import { FormControlLabel, Grid, SelectChangeEvent, useEventCallback } from '@mui/material';
import { memo, useEffect, useMemo } from 'react';
import { FormikErrors, FormikTouched } from 'formik';
import { SectionVM } from 'civil-status/view-models/step-form';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker, TimePicker } from '@mui/lab';
import { ItemVM } from 'shell/view-models/item';
import ListAnsc from '../../../../../../shared/list-ansc';
import { ID_OTHER_REASON } from 'civil-status/constants/id-other-reason';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import { Checkbox } from '@mui/material';
import ListDxSx from 'shell/components/shared/list-dx-sx';
import ListEmergencyRegister from '../../../../../../shared/list-emergency-register';

interface GeneralDataProps {
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  onChanges: (values: SectionVM) => void;
  configuration?: string;
  tipoEvento?: string;
}

const GeneralDataComponent = ({
  disabled = false,
  values,
  errors,
  touched,
  onChange,
  onChanges,
  configuration,
  tipoEvento,
}: GeneralDataProps): React.ReactElement => {
  const isDgrecup2 = useMemo(() => {
    return configuration === ConfigurationType.DGRECUP2;
  }, [configuration]);

  const isDgrecup1 = useMemo(() => {
    return configuration === ConfigurationType.DGRECUP1;
  }, [configuration]);

  const handleListReasonRecoveryChange = useEventCallback((item?: ItemVM) => {
    onChanges({
      ...(values || {}),
      reasonRecovery: item?.id?.toString() || '',
      descriptionsReasonRecovery: '',
    });
  });

  const handleListEmergencyRegister = useEventCallback((item?: ItemVM) => {
    onChanges({
      ...(values || {}),
      emergencyRegister: item,
      number: '',
      identifier: '',
    });
  });

  const handleListDxSx = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('margin', event.target.value);
  });

  useEffect(() => {
    if (values?.number && values?.emergencyRegister?.value) {
      const currNumber =
        values?.number.length >= 5 ? values?.number : values?.number.padStart(5, '0');
      onChange('identifier', `${values.emergencyRegister?.value} - Atto numero ${currNumber}`);
    }
  }, [values?.number, values?.emergencyRegister?.value]);

  return (
    <Grid container spacing={2} item>
      <Grid item xs={12} md={4}>
        <DatePicker
          label={`Data decorrenza ${isDgrecup2 ? '*' : ''}`}
          aria-label="Data decorrenza"
          value={values?.effectiveDate || null}
          onChange={(date) => {
            onChange('effectiveDate', date);
          }}
          disabled={disabled}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="effectiveDate"
              fullWidth
              helperText={errors?.effectiveDate}
              error={touched?.effectiveDate && Boolean(errors?.effectiveDate)}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TimePicker
          label={`Ora decorrenza ${
            values?.effectiveDate && values?.effectiveDate.isValid ? '*' : ''
          }`}
          aria-label="Ora decorrenza"
          value={values?.effectiveTime || null}
          onChange={(date) => {
            onChange('effectiveTime', date);
          }}
          disabled={disabled}
          renderInput={(params) => (
            <PmiTextField
              id="effectiveTime"
              {...params}
              fullWidth
              error={touched?.effectiveTime && Boolean(errors?.effectiveTime)}
              helperText={errors?.effectiveTime}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <ListAnsc
          label="Motivo recupero"
          idDominio="96"
          value={values?.reasonRecovery || ''}
          onChange={handleListReasonRecoveryChange}
          disabled={disabled}
          error={Boolean(errors?.reasonRecovery) && touched?.reasonRecovery}
        />
      </Grid>
      <Grid item xs={12} md={8}>
        <PmiTextField
          label={`Descrizione recupero ${values?.reasonRecovery !== ID_OTHER_REASON ? '' : '*'}`}
          aria-label="Descrizione recupero"
          name="descriptionsReasonRecovery"
          id="descriptionsReasonRecovery"
          fullWidth
          value={values?.descriptionsReasonRecovery || ''}
          onChange={(event) => onChange('descriptionsReasonRecovery', event.target.value)}
          error={touched?.descriptionsReasonRecovery && Boolean(errors?.descriptionsReasonRecovery)}
          helperText={errors?.descriptionsReasonRecovery}
          disabled={disabled || values?.reasonRecovery !== ID_OTHER_REASON}
        />
      </Grid>
      {isDgrecup2 ? (
        <Grid container spacing={2} item>
          <Grid item xs={12} md={4}>
            <ListEmergencyRegister
              label="Registro di emergenza"
              required
              value={values?.emergencyRegister?.id?.toString() || ''}
              onChange={handleListEmergencyRegister}
              disabled={disabled}
              error={Boolean(errors?.emergencyRegister) && touched?.emergencyRegister}
              tipoEvento={tipoEvento}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <ListDxSx
              label="Margine"
              required
              value={values?.margin || ''}
              onChange={handleListDxSx}
              disabled={disabled}
              error={Boolean(errors?.margin) && touched?.margin}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <PmiTextField
              label="Numero atto"
              aria-label="Numero atto"
              id="number"
              fullWidth
              onChange={(event) => {
                onChange('number', event.target.value);
              }}
              value={values?.number || ''}
              helperText={errors?.number}
              error={touched?.number && Boolean(errors?.number)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              label="Identificativo atto cartaceo"
              aria-label="Identificativo atto cartaceo"
              id="identifier"
              fullWidth
              onChange={(event) => {
                onChange('identifier', event.target.value);
              }}
              value={values?.identifier || ''}
              helperText={errors?.identifier}
              error={touched?.identifier && Boolean(errors?.identifier)}
              disabled
            />
          </Grid>
        </Grid>
      ) : undefined}
      {isDgrecup1 ? (
        <Grid item container xs={12}>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  id="isSimpleCertificate"
                  checked={values?.isSimpleCertificate || false}
                  onChange={() => {
                    onChange('isSimpleCertificate', !values?.isSimpleCertificate);
                  }}
                  inputProps={{ 'aria-label': 'Certificato semplice' }}
                  disabled={disabled}
                />
              }
              label="Certificato semplice"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  id="isAbstractForSummary"
                  checked={values?.isAbstractForSummary || false}
                  onChange={() => {
                    onChange('isAbstractForSummary', !values?.isAbstractForSummary);
                  }}
                  inputProps={{ 'aria-label': 'Estratto per riassunto' }}
                  disabled={disabled}
                />
              }
              label="Estratto per riassunto"
            />
          </Grid>
        </Grid>
      ) : undefined}
    </Grid>
  );
};

GeneralDataComponent.displayName = 'Form General Data component';

const GeneralData = memo(GeneralDataComponent);

export default GeneralData;
